import React, { useCallback, useEffect, useState } from 'react';

import CheckOutlinedIcon from '@mui/icons-material/CheckOutlined';
import ClearOutlinedIcon from '@mui/icons-material/ClearOutlined';
import DescriptionSharpIcon from '@mui/icons-material/DescriptionSharp';
import { useTheme } from '@mui/material/styles';
import { ThreeDotMenu } from 'common/components/core';
import {
  Card,
  Grid,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
  Typography,
} from 'common/components/material';
import { CardContent } from 'common/components/material/Card';
import { useDrawer } from 'common/hooks';
import { DrawerIdEnum } from 'features/org-root/enums/drawer-id.enum';
import { Skill } from 'features/skills';
import { useAddSkillFeedback } from '../hooks/useAddSkillFeedback';
import { useDeleteSkillFeedback } from '../hooks/useDeleteSkillFeedback';
import { useGetPastSkillFeedbacks } from '../hooks/useGetPastSkillFeedback';
import {
  FEEDBACK_MENU_OPTIONS,
  FeedbackList,
  PastFeedbackDrawer,
  VoteType,
} from '../matching-engine.utils';
import { SkillFeedbackButtons } from './SkillEndorsementButtons';

const MIN_INPUT_LENGTH = 4;

export const SkillFeedbackCard: React.FC<{
  skill: Skill;
  currentVoteType: VoteType | null;
  currentFeedbackCount: number;
  revieweeId: string;
  canArchive?: boolean;
  skillFeedbackCaptionNode: React.ReactNode;
}> = ({
  skill,
  revieweeId,
  currentVoteType,
  currentFeedbackCount,
  skillFeedbackCaptionNode,
  canArchive = true,
}) => {
  const { palette } = useTheme();
  const { openDrawer } = useDrawer();
  const [feedbackText, setFeedbackText] = useState('');
  const [voteType, setVoteType] = useState<VoteType | null>(currentVoteType);
  const [feedbackCount, setFeedbackCount] = useState(currentFeedbackCount);
  const [draftVoteType, setDraftVoteType] = useState<VoteType | null>(currentVoteType);

  const { mutateAsync: addSkillFeedback, isSuccess } = useAddSkillFeedback();

  const handleSubmit = useCallback(async () => {
    if (!voteType) return;
    await addSkillFeedback({
      revieweeId,
      voteType,
      feedback: feedbackText,
      skillId: skill.id,
    });
    setVoteType(voteType);
    setDraftVoteType(voteType);
    handleClear();
  }, [voteType, feedbackText, skill.id, revieweeId, addSkillFeedback]);

  const handleClear = () => {
    setFeedbackText('');
  };

  useEffect(() => {
    if (!isSuccess) return;
    setFeedbackCount((prev) => prev + 1);
  }, [isSuccess, setFeedbackCount]);

  useEffect(() => {
    if (
      feedbackText.length === 0 &&
      voteType !== draftVoteType &&
      (voteType === VoteType.UPVOTE || voteType === VoteType.DOWNVOTE || VoteType.ARCHIVE)
    ) {
      const timeout = setTimeout(handleSubmit, 3000);
      return () => clearTimeout(timeout);
    }
  }, [voteType, draftVoteType, feedbackText, handleSubmit]);

  const openShowPreviousFeedbackDrawer = () => {
    openDrawer(DrawerIdEnum.PAST_SKILL_FEEDBACKS, {
      revieweeId,
      skill,
    });
  };

  return (
    <Card sx={{ border: 'none' }}>
      <CardContent
        sx={{
          margin: '0.3rem',
          paddingBottom: '0.1rem !important',
          paddingTop: '0.25rem',
        }}
      >
        <Grid
          container
          display="flex"
          direction="row"
          alignItems="center"
          justifyContent="space-between"
        >
          <Grid item lg={4} xl={4}>
            <Typography
              variant="body2"
              fontWeight="600"
              fontSize="0.875rem"
              color={palette.Text.Headline}
            >
              {skill.name}
            </Typography>
            {skillFeedbackCaptionNode}
          </Grid>
          <Grid item lg={2.5} xl={2}>
            <SkillFeedbackButtons
              value={voteType}
              onSelect={setVoteType}
              canArchive={canArchive}
            />
          </Grid>
          <Grid item ml={1} lg={feedbackCount ? 4.5 : 5} xl={feedbackCount ? 5 : 5.5}>
            <AddFeedbackText
              handleSubmit={handleSubmit}
              handleClear={handleClear}
              feedbackText={feedbackText}
              onChange={setFeedbackText}
            />
          </Grid>
          {!!feedbackCount && (
            <Grid item md={0.5}>
              <Stack
                onClick={openShowPreviousFeedbackDrawer}
                sx={{ cursor: 'pointer' }}
                alignItems="center"
              >
                <IconButton
                  sx={{
                    color: palette.Icons.Tertiary.IconFillDefault,
                    padding: 0.2,
                  }}
                >
                  <DescriptionSharpIcon fontSize="small" />
                </IconButton>
                <Typography variant="caption" sx={{ color: palette.Text.SubHeadline }}>
                  {feedbackCount}
                </Typography>
              </Stack>
            </Grid>
          )}
        </Grid>
      </CardContent>
    </Card>
  );
};

export const AddFeedbackText: React.FC<{
  feedbackText: string;
  handleSubmit: () => void;
  handleClear: () => void;
  onChange: Function;
}> = ({ handleSubmit, handleClear, feedbackText, onChange }) => {
  const { palette } = useTheme();
  const handleKeyDown = (event: React.KeyboardEvent) => {
    if ((feedbackText ?? '').length >= MIN_INPUT_LENGTH && event.key === 'Enter') {
      handleSubmit();
    }
  };

  return (
    <TextField
      placeholder="Add note..."
      fullWidth
      variant="outlined"
      size="small"
      value={feedbackText}
      onKeyDown={handleKeyDown}
      onChange={(e) => onChange(e.target.value)}
      InputProps={{
        endAdornment:
          (feedbackText ?? '').length >= MIN_INPUT_LENGTH ? (
            <InputAdornment position="end">
              <CheckOutlinedIcon onClick={handleSubmit} cursor="pointer" fontSize="small" />
              <ClearOutlinedIcon onClick={handleClear} cursor="pointer" fontSize="small" />
            </InputAdornment>
          ) : (
            <></>
          ),
      }}
      sx={{
        '& .MuiOutlinedInput-root': {
          borderRadius: '8px',
          '&:hover fieldset': {
            borderColor: palette.TextFields.Hover,
          },
          '&.Mui-focused fieldset': {
            borderColor: palette.TextFields.Clicked,
          },
        },
        '& .MuiInputBase-input::placeholder': {
          fontWeight: 400,
          opacity: 0.8,
          fontSize: '0.875rem',
          color: palette.EmptyPlaceholderIcons.IconFill,
        },
      }}
    />
  );
};

export const PastSkillFeedbackDrawer: React.FC<{
  revieweeId: any;
  skill: Skill;
}> = ({ revieweeId, skill }) => {
  const { data, isLoading } = useGetPastSkillFeedbacks(revieweeId, skill.id);

  if (isLoading || !data || !data.pastFeedbacks) return <></>;

  const { pastFeedbacks } = data;
  return (
    <PastFeedbackDrawer
      revieweeId={revieweeId}
      drawerId={DrawerIdEnum.PAST_SKILL_FEEDBACKS}
      title={skill.name}
    >
      <FeedbackList feedbacks={pastFeedbacks} />
    </PastFeedbackDrawer>
  );
};

export const PastFeedbackDropdownMenu: React.FC<{
  profileId: string;
  feedbackId: string;
  skillId: string;
  onDelete?: Function;
}> = ({ profileId, feedbackId, onDelete, skillId }) => {
  const { openDrawer, closeDrawer } = useDrawer();

  const { mutateAsync: deleteFeedbacks } = useDeleteSkillFeedback();
  const { refetch: refetchPastFeedbacks } = useGetPastSkillFeedbacks(profileId, skillId);

  const openDeleteConfirmation = () => {
    openDrawer(DrawerIdEnum.LEAVE_FORM, {
      headerTitle: 'Delete Feedback?',
      confirmButtonText: 'Delete',
      subTitle: 'Feedback will be permanently deleted',
      confirmButtonHandler: async () => {
        await deleteFeedbacks(feedbackId);
        refetchPastFeedbacks();
        closeDrawer(DrawerIdEnum.LEAVE_FORM);
        onDelete && onDelete();
      },
    });
  };

  const handleSelect = () => {
    openDeleteConfirmation();
  };

  return <ThreeDotMenu items={FEEDBACK_MENU_OPTIONS} onSelect={handleSelect} />;
};
